import clsx from "clsx";
import { type ReactNode, useState } from "react";
import {
  getEarliestVersion,
  getVersionRange,
  type ActionLogItem,
} from "./getEventLogItems";

import { getUserShortname } from "@engine/round/accessors";
import { type RoundAggregate } from "@engine/round/aggregate";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@sfc/ui";
import { ChevronDownIcon, MoreHorizontalIcon, Trash2Icon } from "lucide-react";

export function ShowEventLogItem({
  round,
  item,
  canDeleteActions = false,
  onRequestDeleteActions,
  showDeleteButton = true,
}: {
  round: RoundAggregate;
  item: ActionLogItem;
  canDeleteActions?: boolean;
  onRequestDeleteActions?: (startVersion: number) => void;
  showDeleteButton?: boolean;
}) {
  const isEventGroup = Array.isArray(item);
  const firstOrOnlyAction = isEventGroup ? item[0] : item;

  if (!firstOrOnlyAction) {
    return <div>Action missing</div>;
  }

  const actingUserShortname = firstOrOnlyAction.actingAsUserId
    ? getUserShortname(round, firstOrOnlyAction.actingAsUserId)
    : "System";
  const underlyingUserShortname = firstOrOnlyAction.underlyingUserId
    ? getUserShortname(round, firstOrOnlyAction.underlyingUserId)
    : "System";
  const userLabel =
    actingUserShortname === underlyingUserShortname ? (
      `User: ${actingUserShortname}`
    ) : (
      <span>
        User: {underlyingUserShortname}
        <span className="text-orange-500 ml-1">
          acting as {actingUserShortname}
        </span>
      </span>
    );

  const type = firstOrOnlyAction.payload.type;
  const summaryWithUser = (
    <>
      {userLabel} -{" "}
      <span className={clsx(type === "NOOP" && "text-orange-500")}>{type}</span>
    </>
  );

  const timestamp: string | undefined =
    "timestamp" in firstOrOnlyAction
      ? (firstOrOnlyAction.timestamp as string)
      : undefined;

  const details = isEventGroup ? (
    <div>
      {item.map((action) => (
        <ShowEventLogItem
          key={action.id}
          round={round}
          item={action}
          canDeleteActions={canDeleteActions}
          onRequestDeleteActions={onRequestDeleteActions}
          showDeleteButton={false}
        />
      ))}
    </div>
  ) : (
    <code>
      <pre>{JSON.stringify(firstOrOnlyAction.payload, null, 2)}</pre>
    </code>
  );

  return (
    <EventLogItemContainer
      timestamp={timestamp}
      summary={summaryWithUser}
      details={details}
      version={getVersionRange(item)}
      deleteStartVersion={getEarliestVersion(item)}
      canDeleteActions={canDeleteActions && showDeleteButton}
      onRequestDeleteActions={onRequestDeleteActions}
    />
  );
}

const EventLogItemContainer = ({
  timestamp,
  summary,
  details,
  version,
  deleteStartVersion,
  canDeleteActions,
  onRequestDeleteActions,
}: {
  timestamp: string | undefined;
  summary: ReactNode;
  details: ReactNode | null;
  version: string;
  deleteStartVersion: number | null;
  canDeleteActions: boolean;
  onRequestDeleteActions?: (startVersion: number) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex items-center p-2 border-b border-stone-700">
      <button
        type="button"
        className="flex items-start"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <ChevronDownIcon
          className={clsx(
            "size-5 transition-transform cursor-pointer",
            isOpen ? "self-start" : "-rotate-90",
          )}
        />
      </button>
      <div className="w-full">
        <button
          type="button"
          className={clsx(
            "flex justify-between text-sm dark:text-stone-300 w-full cursor-pointer",
          )}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>{summary}</span>
          <div className="flex flex-row gap-2">
            <span>{version}</span>
            <span>{timestamp && new Date(timestamp).toLocaleString()}</span>
          </div>
        </button>

        {isOpen && <div className="text-xs mt-1">{details}</div>}
      </div>
      {canDeleteActions && deleteStartVersion != null && (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="ml-2 size-8 shrink-0 text-muted-foreground hover:text-foreground"
                aria-label="Action log item actions"
              />
            }
          >
            <MoreHorizontalIcon className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-48">
            <DropdownMenuItem
              variant="destructive"
              onClick={() => onRequestDeleteActions?.(deleteStartVersion)}
            >
              <Trash2Icon className="size-4" />
              Delete from {deleteStartVersion}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};
